<script setup lang="ts">
import { onMounted, useTemplateRef } from "vue";

import type { BaseButtonProps } from "@/components/ui/BaseButton/BaseButton.vue";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import { useNativeEvents } from "@/composables/useNativeEvents";
import type { Icon } from "@/model/icon";
import { firedWithinElement } from "@/utils/dom/dom";

defineOptions({ inheritAttrs: false });

withDefaults(
  defineProps<{
    value: string;
    icon?: Icon;
    variant?: BaseButtonProps["variant"];
    size?: BaseButtonProps["size"];
  }>(),
  { icon: undefined, variant: "outlined", size: "medium" },
);

const triggerRef = useTemplateRef("trigger");
const buttonRef = useTemplateRef("button");
defineExpose({ focus: () => void buttonRef.value?.focus() });

const { addEventListener } = useNativeEvents();

onMounted(() => {
  addEventListener(document, "keydown", keydown, { capture: true });
});

function keydown(e: KeyboardEvent) {
  if (e.key === "Escape") {
    if (firedWithinElement(e, triggerRef.value)) {
      e.stopImmediatePropagation();
    }
  }
}
</script>

<template>
  <div ref="trigger">
    <BaseButton
      ref="button"
      :variant
      :size
      color="grey"
      class="dropdown-trigger"
      data-dropdown-trigger
      :icon-before="icon"
      icon-after="arrow/chevron-down"
      v-bind="$attrs"
    >
      <span>
        <slot>{{ value }}</slot>
      </span>
    </BaseButton>
  </div>
</template>

<style lang="scss">
.base-button.dropdown-trigger {
  display: flex;
  justify-content: space-between;
  max-width: 100%;
}
</style>
